import { createFileRoute, Link } from "@tanstack/react-router";
import { guidesByCategory } from "@/data/guides";
import { breadcrumbSchema, jsonLd, absUrl } from "@/lib/seo";
import { GENERIC_BOKADIREKT } from "@/lib/booking";
import { InternalLinkHub } from "@/components/InternalLinkHub";


export const Route = createFileRoute("/guider/")({
  head: () => ({
    meta: [
      { title: "Guider om herrfrisyrer & skägg | Tailor's Barber" },
      { name: "description", content: "Guider om herrfrisyrer, fade, skägg, priser och mer – från Tailor's Barber. Tips och råd för att alltid se välvårdad ut." },
      { property: "og:title", content: "Guider & tips | Tailor's Barber" },
      { property: "og:description", content: "Guider om herrfrisyrer, fade, skäggvård och priser – råd från barberarna på Tailor's Barber i Stockholm." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: absUrl("/guider") },
    ],
    links: [{ rel: "canonical", href: absUrl("/guider") }],
    scripts: [
      jsonLd(breadcrumbSchema([
        { name: "Hem", url: "/" },
        { name: "Guider", url: "/guider" }
      ]))
    ]
  }),
  component: GuidesIndexPage,
});

function GuidesIndexPage() {
  const categories = Object.keys(guidesByCategory);

  return (
    <div className="min-h-screen bg-background">
      <div className="container-page py-4">
        <nav className="flex items-center gap-2 text-sm text-foreground/50">
          <Link to="/" className="hover:text-foreground transition-colors">Hem</Link>
          <span>/</span>
          <span className="text-foreground/70">Guider</span>
        </nav>
      </div>

      <header className="container-page pb-12">
        <h1 className="text-3xl md:text-5xl font-semibold mb-4">Guider & tips</h1>
        <p className="text-lg text-foreground/70 max-w-2xl">
          Råd om herrfrisyrer, fade-tekniker, skäggvård, priser och hur du hittar rätt barberare i Stockholm.
        </p>
      </header>

      <section className="container-page pb-16">
        <div className="space-y-14">
          {categories.map((cat) => (
            <div key={cat}>
              <h2 className="text-xl font-semibold mb-6 text-brass">{cat}</h2>
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {guidesByCategory[cat].map((guide) => (
                  <Link
                    key={guide.slug}
                    to="/guider/$slug/"
                    params={{ slug: guide.slug }}
                    className="group block p-5 border border-border rounded-xl hover:border-brass/50 transition-all hover:shadow-sm"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-medium uppercase tracking-wide text-foreground/50">{guide.category}</span>
                      {guide.readMinutes && (
                        <span className="text-xs text-foreground/40">• {guide.readMinutes} min</span>
                      )}
                    </div>
                    <h3 className="font-semibold leading-snug group-hover:text-brass transition-colors mb-2">
                      {guide.title}
                    </h3>
                    <p className="text-sm text-foreground/60 leading-relaxed line-clamp-2">{guide.excerpt}</p>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <InternalLinkHub />

      <section className="container-page py-12 border-t border-border">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold mb-1">Redo att boka?</h2>
            <p className="text-foreground/60">Fyra salonger i Stockholm. Boka online på 30 sekunder.</p>
          </div>
          <a
            href={GENERIC_BOKADIREKT}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-brass whitespace-nowrap"
          >
            Boka tid nu
          </a>
        </div>
      </section>

    </div>
  );
}
